import { crossedRim } from "./physics";
import { ROUND_SECONDS, SeededRandom, pointsForDistance } from "./rules";

export type RankedMode = "moving" | "daily";
export type RankedThrow = [
  tick: number,
  athleteId: number,
  x: number,
  y: number,
  velocityX: number,
  velocityY: number,
  grabTick: number,
  grabX: number,
];

export const WIDTH = 1_280;
export const HEIGHT = 720;
export const GROUND_Y = 610;
export const GRAVITY = 1_850;
export const MAX_THROW_SPEED = 1_580;
export const FIXED_STEP = 1 / 120;
export const ROUND_TICKS = ROUND_SECONDS * 120;
export const MAX_RANKED_THROWS = 120;
export const MAX_ATHLETE_COLLISIONS = 10;
export const WALK_ZONE_MIN_X = 75;
export const WALK_ZONE_MAX_X = 855;
export const BASE_RIM = { x: 1_073, y: 253, innerWidth: 94, halfWidth: 52 } as const;
export const ENVIRONMENT_SEED_XOR = 0x243f6a88;
export const GAMEPLAY_SEED_XOR = 0x9e3779b9;
export const RIM_TRAVEL = 78;

const ATHLETE_COUNT = 7;
const SETTLE_RESPAWN_TICKS = 22;
const RELEASE_EXCLUSION_X_PADDING = 48;
const RELEASE_EXCLUSION_BOTTOM = BASE_RIM.y + 185;

interface Body {
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  velocityX: number;
  velocityY: number;
  points: number;
  collisions: number;
  scored: boolean;
}

interface RankedAthlete {
  id: number;
  phase: "walking" | "held" | "airborne" | "respawning";
  x: number;
  velocityX: number;
  direction: -1 | 1;
  turnTimer: number;
  respawnTicks: number;
  heldTraceIndex: number;
  points: number;
  body: Body | null;
}

export interface RankedSimulationResult {
  valid: boolean;
  score: number;
  error?: string;
}

export function athleteSeed(seed: number, athleteId: number): number {
  return (seed ^ GAMEPLAY_SEED_XOR ^ Math.imul(athleteId + 1, 0x85ebca6b)) >>> 0;
}

export function rankedRimXAtTick(tick: number, rimPhase: number): number {
  return BASE_RIM.x + Math.sin(tick * FIXED_STEP * 1.15 + rimPhase) * RIM_TRAVEL;
}

export function isLegalReleasePoint(x: number, y: number, rimX: number): boolean {
  if (y <= BASE_RIM.y) return false;
  const exclusionLeft = rimX - BASE_RIM.halfWidth - RELEASE_EXCLUSION_X_PADDING;
  const exclusionRight = rimX + 125 + RELEASE_EXCLUSION_X_PADDING;
  return x < exclusionLeft || x > exclusionRight || y > RELEASE_EXCLUSION_BOTTOM;
}

export function clampToLegalReleasePoint(x: number, y: number, rimX: number): { x: number; y: number } {
  const legalY = Math.max(y, BASE_RIM.y + 0.01);
  if (isLegalReleasePoint(x, legalY, rimX)) return { x, y: legalY };
  const left = rimX - BASE_RIM.halfWidth - RELEASE_EXCLUSION_X_PADDING - 0.01;
  const right = rimX + 125 + RELEASE_EXCLUSION_X_PADDING + 0.01;
  return Math.abs(x - left) <= Math.abs(x - right) ? { x: left, y: legalY } : { x: right, y: legalY };
}

export function quantizeLegalReleasePoint(x: number, y: number, rimX: number): { x: number; y: number } {
  let point = { x: roundTransport(x), y: roundTransport(y) };
  if (isLegalReleasePoint(point.x, point.y, rimX)) return point;
  point = clampToLegalReleasePoint(point.x, point.y, rimX);
  return { x: roundTransport(point.x), y: roundTransport(point.y) };
}

export function quantizeRankedThrow(
  tick: number,
  athleteId: number,
  x: number,
  y: number,
  velocityX: number,
  velocityY: number,
  grabTick: number,
  grabX: number,
): RankedThrow {
  return [
    tick,
    athleteId,
    roundTransport(x),
    roundTransport(y),
    roundTransport(velocityX),
    roundTransport(velocityY),
    grabTick,
    roundTransport(grabX),
  ];
}

export function parseRankedTrace(value: unknown): RankedThrow[] {
  if (!Array.isArray(value) || value.length > MAX_RANKED_THROWS) throw new Error("Invalid throw trace");
  return value.map((entry): RankedThrow => {
    if (!Array.isArray(entry) || entry.length !== 8 || entry.some((part) => typeof part !== "number" || !Number.isFinite(part))) {
      throw new Error("Invalid throw trace");
    }
    const [tick, athleteId, x, y, velocityX, velocityY, grabTick, grabX] = entry;
    if (
      !Number.isInteger(tick) || tick < 1 || tick > ROUND_TICKS ||
      !Number.isInteger(athleteId) || athleteId < 0 || athleteId >= ATHLETE_COUNT ||
      !Number.isInteger(grabTick) || grabTick < 0 || grabTick >= tick ||
      Math.abs(x) > WIDTH * 2 || y < -HEIGHT || y > HEIGHT * 2 ||
      Math.hypot(velocityX, velocityY) > MAX_THROW_SPEED * 1.01 ||
      grabX < WALK_ZONE_MIN_X || grabX > WALK_ZONE_MAX_X
    ) throw new Error("Invalid throw trace");
    return [tick, athleteId, x, y, velocityX, velocityY, grabTick, grabX];
  });
}

export function simulateRankedTrace(_mode: RankedMode, seed: number, trace: readonly RankedThrow[]): RankedSimulationResult {
  void _mode;
  if (trace.length > MAX_RANKED_THROWS) return invalid("Too many throws");
  const sorted = [...trace].sort((left, right) => left[0] - right[0]);
  if (sorted.some((entry, index) => entry !== trace[index])) return invalid("Throws are not ordered");

  const environmentRandom = new SeededRandom(seed ^ ENVIRONMENT_SEED_XOR);
  const rimPhase = environmentRandom.next() * Math.PI * 2;
  const athleteRandoms = Array.from({ length: ATHLETE_COUNT }, (_, athleteId) => new SeededRandom(athleteSeed(seed, athleteId)));
  const athletes = athleteRandoms.map((random, athleteId) => createAthlete(athleteId, random));
  const grabsByTick = new Map<number, number[]>();

  for (let traceIndex = 0; traceIndex < trace.length; traceIndex += 1) {
    const event = trace[traceIndex];
    if (!event) continue;
    const grabIndexes = grabsByTick.get(event[6]) ?? [];
    grabIndexes.push(traceIndex);
    grabsByTick.set(event[6], grabIndexes);
  }

  let traceIndex = 0;
  let rimX = rankedRimXAtTick(0, rimPhase);
  let previousRimX = rimX;
  let wind = 0;
  let windTarget = 0;
  let windTimer = 0;
  let score = 0;

  const processGrabs = (tick: number): string | null => {
    for (const eventIndex of grabsByTick.get(tick) ?? []) {
      const event = trace[eventIndex];
      if (!event) return "Missing grab event";
      const athlete = athletes[event[1]];
      if (!athlete || athlete.phase !== "walking") return "Athlete was grabbed while unavailable";
      const canonicalGrabX = roundTransport(athlete.x);
      if (Math.abs(canonicalGrabX - event[7]) > 0.05) return "Grab origin does not match the seeded athlete";
      athlete.phase = "held";
      athlete.heldTraceIndex = eventIndex;
      athlete.points = pointsForDistance(canonicalGrabX, rimX);
    }
    return null;
  };

  const initialGrabError = processGrabs(0);
  if (initialGrabError) return invalid(initialGrabError);

  for (let tick = 1; tick <= ROUND_TICKS; tick += 1) {
    while (trace[traceIndex]?.[0] === tick) {
      const event = trace[traceIndex];
      if (!event) break;
      const [, athleteId, x, y, velocityX, velocityY] = event;
      const athlete = athletes[athleteId];
      if (
        !athlete || athlete.phase !== "held" || athlete.heldTraceIndex !== traceIndex ||
        !isLegalReleasePoint(x, y, rimX)
      ) return invalid("Athlete was thrown while unavailable or outside the legal court");
      athlete.phase = "airborne";
      athlete.body = {
        x,
        y,
        previousX: x,
        previousY: y,
        velocityX,
        velocityY,
        points: athlete.points,
        collisions: 0,
        scored: false,
      };
      traceIndex += 1;
    }

    previousRimX = rimX;
    rimX = rankedRimXAtTick(tick, rimPhase);
    windTimer -= FIXED_STEP;
    if (windTimer <= 0) {
      windTarget = (environmentRandom.next() * 2 - 1) * 185;
      windTimer = 3.8 + environmentRandom.next() * 3.4;
    }
    wind += (windTarget - wind) * (1 - Math.exp(-FIXED_STEP * 0.85));

    for (const athlete of athletes) {
      const random = athleteRandoms[athlete.id];
      if (!random) return invalid("Missing athlete random stream");

      if (athlete.phase === "respawning") {
        athlete.respawnTicks -= 1;
        if (athlete.respawnTicks <= 0) resetAthlete(athlete, random);
        continue;
      }
      if (athlete.phase === "held") continue;
      if (athlete.phase === "walking") {
        updateWalkingAthlete(athlete, random);
        continue;
      }

      const body = athlete.body;
      if (!body) return invalid("Missing airborne athlete body");
      body.previousX = body.x;
      body.previousY = body.y;
      body.velocityY += GRAVITY * FIXED_STEP;
      body.velocityX += wind * FIXED_STEP;
      body.velocityX *= Math.pow(0.998, FIXED_STEP * 120);
      body.x += body.velocityX * FIXED_STEP;
      body.y += body.velocityY * FIXED_STEP;

      if (!body.scored && crossedRim({
        previousX: body.previousX - previousRimX,
        previousY: body.previousY,
        x: body.x - rimX,
        y: body.y,
        velocityY: body.velocityY,
        rimX: 0,
        rimY: BASE_RIM.y,
        innerWidth: BASE_RIM.innerWidth,
      })) {
        score += body.points;
        body.scored = true;
        continue;
      }

      if (resolveRimCollision(body, rimX - BASE_RIM.halfWidth, BASE_RIM.y) && recordCollision(athlete)) continue;
      if (resolveRimCollision(body, rimX + BASE_RIM.halfWidth, BASE_RIM.y) && recordCollision(athlete)) continue;
      if (resolveBackboardCollision(body, rimX) && recordCollision(athlete)) continue;

      if (body.y >= GROUND_Y - 36 && body.velocityY > 0) {
        body.y = GROUND_Y - 36;
        if (recordCollision(athlete)) continue;
        if (Math.abs(body.velocityY) > 260) {
          body.velocityY *= -0.28;
          body.velocityX *= 0.72;
        } else {
          queueRespawn(athlete, SETTLE_RESPAWN_TICKS);
          continue;
        }
      }
      if (body.x < -120 || body.x > WIDTH + 120 || body.y > HEIGHT + 160) resetAthlete(athlete, random);
    }

    if (tick < ROUND_TICKS) {
      const grabError = processGrabs(tick);
      if (grabError) return invalid(grabError);
    }
  }

  if (traceIndex !== trace.length) return invalid("Throw occurs after the round");
  return { valid: true, score };
}

function createAthlete(id: number, random: SeededRandom): RankedAthlete {
  const athlete: RankedAthlete = {
    id,
    phase: "walking",
    x: 0,
    velocityX: 0,
    direction: 1,
    turnTimer: 0,
    respawnTicks: 0,
    heldTraceIndex: -1,
    points: 1,
    body: null,
  };
  resetAthlete(athlete, random);
  return athlete;
}

function resetAthlete(athlete: RankedAthlete, random: SeededRandom): void {
  athlete.x = WALK_ZONE_MIN_X + 20 + athlete.id * 108 + random.next() * 24;
  athlete.direction = random.next() < 0.5 ? -1 : 1;
  athlete.velocityX = athlete.direction * (40 + random.next() * 35);
  athlete.turnTimer = 1 + random.next() * 4;
  athlete.phase = "walking";
  athlete.respawnTicks = 0;
  athlete.heldTraceIndex = -1;
  athlete.points = 1;
  athlete.body = null;
}

function updateWalkingAthlete(athlete: RankedAthlete, random: SeededRandom): void {
  athlete.turnTimer -= FIXED_STEP;
  if (athlete.turnTimer <= 0) {
    athlete.turnTimer = 1.2 + random.next() * 3.4;
    if (random.next() < 0.35) athlete.direction = athlete.direction === 1 ? -1 : 1;
    athlete.velocityX = athlete.direction * (38 + random.next() * 42);
  }
  athlete.x += athlete.velocityX * FIXED_STEP;
  if (athlete.x < WALK_ZONE_MIN_X) {
    athlete.x = WALK_ZONE_MIN_X;
    athlete.direction = 1;
    athlete.velocityX = Math.abs(athlete.velocityX);
  }
  if (athlete.x > WALK_ZONE_MAX_X) {
    athlete.x = WALK_ZONE_MAX_X;
    athlete.direction = -1;
    athlete.velocityX = -Math.abs(athlete.velocityX);
  }
}

function queueRespawn(athlete: RankedAthlete, ticks: number): void {
  athlete.phase = "respawning";
  athlete.respawnTicks = ticks;
  athlete.body = null;
}

function recordCollision(athlete: RankedAthlete): boolean {
  const body = athlete.body;
  if (!body) return false;
  body.collisions += 1;
  if (body.collisions < MAX_ATHLETE_COLLISIONS) return false;
  queueRespawn(athlete, SETTLE_RESPAWN_TICKS);
  return true;
}

function resolveRimCollision(body: Body, rimX: number, rimY: number): boolean {
  const radius = 27;
  const dx = body.x - rimX;
  const dy = body.y - rimY;
  const distance = Math.hypot(dx, dy);
  if (distance >= radius || distance === 0) return false;
  const normalX = dx / distance;
  const normalY = dy / distance;
  body.x = rimX + normalX * radius;
  body.y = rimY + normalY * radius;
  const projection = body.velocityX * normalX + body.velocityY * normalY;
  if (projection < 0) {
    body.velocityX -= 1.58 * projection * normalX;
    body.velocityY -= 1.58 * projection * normalY;
  }
  return true;
}

function resolveBackboardCollision(body: Body, rimX: number): boolean {
  const boardX = rimX + 97;
  if (
    body.previousX + 23 > boardX || body.x + 23 < boardX ||
    body.y <= 125 || body.y >= 279 || body.velocityX <= 0
  ) return false;
  body.x = boardX - 24;
  body.velocityX *= -0.55;
  return true;
}

function roundTransport(value: number): number {
  return Math.round(value * 100) / 100;
}

function invalid(error: string): RankedSimulationResult {
  return { valid: false, score: 0, error };
}
